import { useEffect } from 'react';
import { DEFAULT_OG_IMAGE, SITE_NAME } from '~/seo/site';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  canonical?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  /** 作为其他页面的子块嵌入时不改写文档头，避免盖掉宿主页 */
  skip?: boolean;
}

const DEFAULT_AUTHOR = '逗赛科技';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let element = document.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export const useSEO = ({
  title,
  description,
  keywords,
  author,
  robots,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  canonical,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  skip,
}: SEOProps) => {
  useEffect(() => {
    if (skip) return;

    const resolvedTitle = ogTitle || title;
    const resolvedDescription = ogDescription || description;
    const resolvedImage = ogImage || twitterImage || DEFAULT_OG_IMAGE;
    const resolvedUrl = canonical || ogUrl;

    if (title) {
      document.title = title;
      upsertMeta('name', 'title', title);
    }

    if (description) upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'author', author || DEFAULT_AUTHOR);
    upsertMeta('name', 'robots', robots || 'index, follow');

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'zh_CN');
    if (resolvedTitle) upsertMeta('property', 'og:title', resolvedTitle);
    if (resolvedDescription) upsertMeta('property', 'og:description', resolvedDescription);
    upsertMeta('property', 'og:image', resolvedImage);
    if (ogUrl) upsertMeta('property', 'og:url', ogUrl);
    if (resolvedUrl) upsertCanonical(resolvedUrl);

    upsertMeta('name', 'twitter:card', twitterCard || 'summary');
    if (ogUrl) upsertMeta('name', 'twitter:url', ogUrl);
    if (resolvedTitle) upsertMeta('name', 'twitter:title', twitterTitle || resolvedTitle);
    if (resolvedDescription) {
      upsertMeta('name', 'twitter:description', twitterDescription || resolvedDescription);
    }
    upsertMeta('name', 'twitter:image', resolvedImage);
  }, [
    title,
    description,
    keywords,
    author,
    robots,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    canonical,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    skip,
  ]);
};

export default useSEO;
